import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import { AdDisplay } from "./AdDisplay";
import { RtbAd } from "./RtbAd";

const AdSpace = ({ zoneId, width, height, refresh = false, subid="" }) => {
  const [containerDimensions, setContainerDimensions] = useState({
    width: width || "100%",
    height: height || "100%",
  });
  const [ad, setAd] = useState(null);
  const containerRef = useRef(null);

  // États pour suivre les dimensions et l'état de la demande
  const [areDimensionsReady, setAreDimensionsReady] = useState(false);
  const [hasRequestedAd, setHasRequestedAd] = useState(false);

  useEffect(() => {
    const updateDimensions = (entries) => {
      if (entries.length > 0) {
        const { width: containerWidth, height: containerHeight } = entries[0].contentRect;

        if (!width && !height) {
          setContainerDimensions({ width: containerWidth, height: containerHeight });
        } else if (width && !height) {
          setContainerDimensions({ width: width, height: containerHeight });
        } else if (!width && height) {
          setContainerDimensions({ width: containerWidth, height: height });
        } else if (width && height) {
          // Cas où les deux dimensions sont spécifiées (incluant 1x1)
          setContainerDimensions({ width: width, height: height });
        }

        // Vérifie si les deux dimensions sont prêtes
        // Pour les dimensions fixes (comme 1x1), elles sont immédiatement prêtes
        if (width && height) {
          setAreDimensionsReady(true);
        } else if (containerWidth !== "100%" && containerHeight !== "100%") {
          setAreDimensionsReady(true);
        }
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [width, height]);

  // Initialisation immédiate pour les dimensions fixes
  useEffect(() => {
    if (width && height) {
      setContainerDimensions({ width: width, height: height });
      setAreDimensionsReady(true);
    }
  }, [width, height]);

  const fetchAd = async () => {
    if (!areDimensionsReady || hasRequestedAd) return;
    console.log("fetching ad");
    setHasRequestedAd(true);
    try {
      const currentUrl = window.location.href;
      const widthInt = parseInt(containerDimensions.width, 10);
      const heightInt = parseInt(containerDimensions.height, 10);

      const newAd = await sdk.models.AdServer.selectDisplayAd({
        zoneId: zoneId,
        size: `${widthInt}x${heightInt}`,
        currentUrl: currentUrl
      });

      setAd(newAd);

      if (newAd === null) {
        sdk.models.AdsImpression.addNoAd({
          zoneId: zoneId,
          size: `${widthInt}x${heightInt}`,
          currentUrl: currentUrl,
          subid: subid
        });
      }
    } catch (error) {
      console.error("Failed to fetch ad:", error);
    }
  };

  useEffect(() => {
    if (areDimensionsReady && !hasRequestedAd) {
      fetchAd();
    }
  }, [areDimensionsReady, hasRequestedAd]);

  // Rafraîchissement automatique des annonces toutes les 30 secondes
  useEffect(() => {
    let intervalId;

    if (refresh) {
      intervalId = setInterval(() => {
        setHasRequestedAd(false);
        setTimeout(function(){
          console.log("fetching new ad");
          fetchAd();
        },1000);
      }, 30000); // 30 secondes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [refresh]);

  const style = {
    width: containerDimensions.width,
    height: containerDimensions.height,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f0f0",
  };

  return (
    <div ref={containerRef} style={style}>
      {ad ? (
        ad.type === "rtb_ad" ? (
          <RtbAd
            adBidId={ad.adBidId}
            ad={ad.rtb_ad} 
            zoneId={zoneId} 
            subid={subid}
            width={containerDimensions.width}
            height={containerDimensions.height}
          />
        ) : (
          <AdDisplay
            zoneId={zoneId}
            showPage={false} 
            ad={ad}
            subid={subid}
            width={containerDimensions.width}
            height={containerDimensions.height}
          />
        )
      ) : (
        <p>Chargement d'une annonce...</p>
      )}
    </div>
  );
};

export { AdSpace };